import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'
import type {ActorHistory} from './teamApi'

// =============================================
// Shared Types (aligned with backend DTOs)
// =============================================

export interface TrendValue {
  value: number
  previousValue?: number
  changePercent?: number
  trend?: 'up' | 'down' | 'stable'
}

export interface StatusBreakdown {
  label: string
  count: number
  color?: string
  /** Share of the breakdown total, 0–100 (1 decimal). Set where the UI shows "% by status". */
  percent?: number
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  pageNumber: number
  pageSize: number
}

// =============================================
// Platform Admin Dashboard Types
// =============================================

export interface PlatformOverviewResponse {
  activeCompanies: TrendValue
  activeAgencies: TrendValue
  activeMsps: TrendValue
  totalBranches: TrendValue
  totalUsers: TrendValue
  activeUsers: TrendValue
  fillRate: TrendValue
  /** Agencies created within the selected date range (0 when no range is set). */
  agenciesOnboarded: number
  /** Companies created within the selected date range (0 when no range is set). */
  companiesOnboarded: number
  activeOrders: TrendValue
  avgTimeToFill?: TrendValue
  avgResponseTime?: TrendValue
}

export interface PlatformQuickInsightsResponse {
  serviceOrdersByStatus: StatusBreakdown[]
  jobOrdersByStatus: StatusBreakdown[]
  jobOrdersByPosition: StatusBreakdown[]
  companiesByStatus: StatusBreakdown[]
  agenciesByStatus: StatusBreakdown[]
  employeesByStatus: StatusBreakdown[]
  usersByRole: StatusBreakdown[]
}

export interface WeeklyBillingPoint {
  week: string
  billed: number
  pay: number
}

export interface EorPayrollBreakdown {
  agencyId: string
  agencyName: string
  billed: number
  pay: number
  margin: number
}

export interface TopSpendClient {
  companyId: string
  companyName: string
  billed: number
}

export interface ActivePayrollBatches {
  count: number
  companyCount: number
}

export interface PlatformFinancialResponse {
  totalBilled: TrendValue
  totalPay: TrendValue
  grossMargin: number
  avgMarkup: number
  outstandingBalance: number
  outstandingCount: number
  oldestInvoiceAgeDays?: number
  spendYTD: TrendValue
  spendMTD: TrendValue
  activePayrollBatches: ActivePayrollBatches
  weeklyBillingTrend: WeeklyBillingPoint[]
  payrollByEor: EorPayrollBreakdown[]
  topSpendByCompany: TopSpendClient[]
  topSpendClient?: TopSpendClient
}

/** Companies Overview roster row (see getPlatformCompaniesOverview for column definitions). */
export interface CompanyOverviewRow {
  companyId: string
  companyName: string
  status: string
  activeOrders: number
  hoursWorked: number
  spendMTD: number
  spendYTD: number
}

/** Agencies Overview roster row (see getPlatformAgenciesOverview for column definitions). */
export interface AgencyOverviewRow {
  agencyId: string
  agencyName: string
  status: string
  candidatePool: number
  activeOrders: number
  avgResponseMs?: number
  hoursWorked: number
}

/** MSPs Overview roster row (see getPlatformMspsOverview for column definitions). */
export interface MspOverviewRow {
  mspId: string
  mspName: string
  branches: number
  activeOrders: number
  hoursWorked: number
  avgResponseMs?: number
  fillRate: number
}

/** Branch Comparison row (see getBranchComparison for metric + window definitions). */
export interface BranchComparisonRow {
  branchId: string
  branchName: string
  mspId: string
  mspName: string
  metric: string
  pastWeek: number
  twoWeeksAgo: number
  difference: number
  trend: 'up' | 'down' | 'stable'
}

export interface BranchSummary {
  branchId: string
  branchName: string
  mspName: string
  location: string
  totalUsers: number
  activeOrders: number
  hoursWorked: number
}

export interface BranchUser {
  name: string
  role: string
  email: string
  status: string
  /**
   * Workers this person accepted onto the branch's filled orders, all time, by user id (S12). Null
   * when the read failed. A server before S12 sends a different figure under this name, its
   * email-keyed assignment count (0 by default), so read it only when the response carries
   * `fillsHistory`.
   */
  totalFills?: number | null
}

export interface BranchDetailResponse {
  summary: BranchSummary
  users: BranchUser[]
  /**
   * From when acceptances carry their acceptor's id, for the Fills column's note. The S12 server always
   * sends it (with nulls when the read failed); a server before S12 never does, which marks its
   * `users[].totalFills` as the legacy count.
   */
  fillsHistory?: ActorHistory
}

export type RecentActivityTab =
  | 'All'
  | 'ServiceOrders'
  | 'JobOrders'
  | 'Companies'
  | 'Agencies'
  | 'Users'

export interface RecentActivityItem {
  id: string
  type: string
  description: string
  entity: string
  by: string
  date: string
  status: string
}

export interface PlatformRecentActivityParams {
  tab: RecentActivityTab
  pageNumber: number
  pageSize: number
  companyId?: string
  mspAgencyId?: string
  branchAgencyId?: string
  startDate?: string
  endDate?: string
}

export interface PlatformOverviewParams {
  companyId?: string
  mspAgencyId?: string
  branchAgencyId?: string
  startDate?: string
  endDate?: string
}

export interface FunnelStage {
  stage: string
  count: number
}

export interface PipelineFunnelResponse {
  serviceOrderFunnel: FunnelStage[]
  jobOrderFunnel: FunnelStage[]
}

export interface AgingBucket {
  label: string
  count: number
  urgency: 'low' | 'medium' | 'high' | 'critical'
}

export interface OrderAgingResponse {
  buckets: AgingBucket[]
  total: number
}

// =============================================
// Company Dashboard Types
// =============================================

export interface CompanyOverviewResponse {
  openOrders: TrendValue
  laborCostPerMonth: TrendValue
  laborCostGrossWages?: TrendValue
  activeEmployees: TrendValue
  avgTimeToFill?: TrendValue
  upcomingExpirations?: number
  spendYTD?: number
}

export interface CompanyRecentActivityParams {
  companyId: string
  pageNumber: number
  pageSize: number
}

// =============================================
// MSP Dashboard Types
// =============================================

export interface MspOverviewResponse {
  activeServiceOrders: TrendValue
  activeJobOrders: TrendValue
  pendingServiceOrders: TrendValue
  avgAgencyResponseTime?: TrendValue
  avgTimeToFill?: TrendValue
  candidatePoolAccuracy?: number
  completedOrdersThisWeek?: TrendValue
  totalHoursWorked?: TrendValue
  upcomingExpirations?: number
}

export interface MspOverviewParams {
  branchAgencyId?: string
  startDate?: string
  endDate?: string
}

/** Selectable window for the MSP Branch Comparison table (STFFLG-3738). */
export type MspBranchComparisonPeriod = 'week' | '12w'

/**
 * STFFLG-3931: an explicit window. Every preset beyond week/12w is computed client-side and sent
 * as from/to, so the backend enum doesn't grow a case per preset. Sending these wins over `period`.
 */
export interface MspBranchComparisonRange {
  from: string
  to: string
}

/** One branch row of the MSP Branch Comparison table (metrics computed over the selected window). */
export interface MspBranchComparisonRow {
  branchId: string
  branchName: string
  openOrders: number
  hoursWorked: number
  // ── STFFLG-3931. `revenue` and `margin` are nullable: margin has nothing to divide by when
  // nothing was billed, and the UI shows a dash rather than implying a real 0%.
  totalOrders: number
  filledOrders: number
  deletedOrders: number
  peoplePlaced: number
  activePlacements: number
  totalPayroll: number
  revenue: number | null
  margin: number | null
  /** Fill rate %, 0–100. */
  fillRate: number
  /** Avg first-response time in ms (null when no data in the window). */
  avgResponseTime: number | null
  /** Avg time-to-fill in days (null when no data in the window). */
  avgTimeToFill: number | null
}

export interface MspBranchComparisonResponse {
  /** STFFLG-3931: the window the backend actually used, so the card can label itself. */
  windowStart?: string
  windowEnd?: string
  period: MspBranchComparisonPeriod
  branches: MspBranchComparisonRow[]
}

// =============================================
// Agency Dashboard Types
// =============================================

export interface AgencyOverviewResponse {
  assignedOrders: TrendValue
  fillRate: TrendValue
  completedOrdersThisWeek: TrendValue
  avgResponseTime?: TrendValue
  upcomingExpirations?: number
  noShowRate?: number
  totalHoursWorked?: number
  /** Attendance rate % (100 - no-show), this week vs last week (STFFLG-3694). */
  attendanceRate?: TrendValue
  candidatePoolAccuracy?: number
  avgTimeToFill?: TrendValue
  /** Service orders distributed to the agency over the trailing 12 weeks. */
  orderVolumeTotal?: number
  /** Active (non-terminated) applicants in the agency candidate pool (STFFLG-3694 #29). */
  activeCandidates?: number
}

export interface AgencyOverviewParams {
  startDate?: string
  endDate?: string
}

// =============================================
// Agency Dashboard: welcome card
// =============================================

/**
 * Spots on job orders by day. Every `date` in the response is YYYY-MM-DD counted from `today`: each
 * order sits on its own site's calendar day, offset from today at its own site.
 */
export interface WelcomeCardSpotsDay {
  date: string
  /** Spots requested. */
  needed: number
  /** Of those, accepted candidates: on a Filled or Closed order, its assignments. Never a pledge. */
  filled: number
}

/** Job orders that closed on `date`, by outcome. */
export interface WelcomeCardClosedDay {
  date: string
  fullyFilled: number
  incomplete: number
  unfilled: number
}

/** The orders the caller had access to that closed on `date`. */
export interface WelcomeCardYouDay {
  date: string
  /**
   * Spots on the orders the caller had access to, in any role (as recorded when the order was
   * worked, else today), or added an accepted candidate to.
   */
  spots: number
  /** Of those, accepted candidates the caller added (Added By); never whoever set the status. */
  filledByYou: number
}

/** Median hours from an order opening to fully filled; null when no order closed fully filled. */
export interface WelcomeCardMedians {
  last7: number | null
  last28: number | null
}

/** Every order of the agency in the top bar, whoever can see it, on the You view's basis. */
export interface WelcomeCardBranch {
  /** 28 rows: today-28 … today-1, by the day each job order closed. */
  days: WelcomeCardSpotsDay[]
  medianHoursToFill: WelcomeCardMedians
}

/** The caller's figures, and the whole branch's to compare with. */
export interface WelcomeCardYou {
  /** 28 rows: today-28 … today-1, by the day each job order closed. */
  days: WelcomeCardYouDay[]
  /** Over the orders the caller had access to. */
  medianHoursToFill: WelcomeCardMedians
  branch: WelcomeCardBranch
}

/**
 * GET /api/dashboard/agency/welcome-card. Every figure counts only the orders the user can see (the
 * lists' Teams visibility; MSP users and platform admins are not filtered), except `you.branch`.
 * Voided orders and backup groups never count.
 */
export interface WelcomeCardResponse {
  /** IANA zone of the agency in the top bar (from its address): the greeting and the date. */
  timeZone: string
  /** Today in timeZone, YYYY-MM-DD: the day every row is counted from. */
  today: string
  /** Exactly what the Job Orders grid's Needs people filter lists. */
  jobOrdersNeedingPeople: number
  /** Pending service orders still short of pledges. */
  serviceOrdersNeedingPledges: number
  /** 14 rows: today … today+13, by shift day. */
  upcoming: WelcomeCardSpotsDay[]
  /** 56 rows: today-56 … today-1, by shift day. */
  past: WelcomeCardSpotsDay[]
  /** 56 rows: today-56 … today-1, by the day each job order closed. */
  closed: WelcomeCardClosedDay[]
  /** Null without a user id, or for an MSP user or platform admin with no fill in the 28 days. */
  you: WelcomeCardYou | null
}

// =============================================
// Dashboard API Service
// =============================================

class DashboardApiService extends BaseApi {
  private readonly basePath = '/api/dashboard'

  // ---- Platform Admin ----

  async getPlatformOverview(params?: PlatformOverviewParams): Promise<PlatformOverviewResponse> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<PlatformOverviewResponse>(
        `${this.basePath}/platform/overview${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getPlatformOverview failed:', error)
      throw error
    }
  }

  async getPlatformQuickInsights(
    params?: PlatformOverviewParams,
  ): Promise<PlatformQuickInsightsResponse> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<PlatformQuickInsightsResponse>(
        `${this.basePath}/platform/quick-insights${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getPlatformQuickInsights failed:', error)
      throw error
    }
  }

  async getPlatformFinancial(params?: PlatformOverviewParams): Promise<PlatformFinancialResponse> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<PlatformFinancialResponse>(
        `${this.basePath}/platform/financial${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getPlatformFinancial failed:', error)
      throw error
    }
  }

  async getPlatformCompaniesOverview(
    params?: PlatformOverviewParams,
  ): Promise<CompanyOverviewRow[]> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<CompanyOverviewRow[]>(
        `${this.basePath}/platform/companies-overview${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getPlatformCompaniesOverview failed:', error)
      throw error
    }
  }

  async getPlatformAgenciesOverview(params?: PlatformOverviewParams): Promise<AgencyOverviewRow[]> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<AgencyOverviewRow[]>(
        `${this.basePath}/platform/agencies-overview${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getPlatformAgenciesOverview failed:', error)
      throw error
    }
  }

  async getPlatformMspsOverview(params?: PlatformOverviewParams): Promise<MspOverviewRow[]> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<MspOverviewRow[]>(
        `${this.basePath}/platform/msps-overview${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getPlatformMspsOverview failed:', error)
      throw error
    }
  }

  async getBranchComparison(
    params?: PlatformOverviewParams & {metric?: 'orders-filled' | 'people-placed'},
  ): Promise<BranchComparisonRow[]> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<BranchComparisonRow[]>(
        `${this.basePath}/platform/branch-comparison${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getBranchComparison failed:', error)
      throw error
    }
  }

  async getBranchDetail(branchId: string): Promise<BranchDetailResponse> {
    try {
      const response = await this.get<BranchDetailResponse>(
        `${this.basePath}/platform/branch-detail?branchId=${encodeURIComponent(branchId)}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getBranchDetail failed:', error)
      throw error
    }
  }

  async getPlatformRecentActivity(
    params: PlatformRecentActivityParams,
  ): Promise<PaginatedResponse<RecentActivityItem>> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<PaginatedResponse<RecentActivityItem>>(
        `${this.basePath}/platform/recent-activity${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getPlatformRecentActivity failed:', error)
      throw error
    }
  }

  async getPipelineFunnel(params?: {
    companyId?: string
    mspAgencyId?: string
  }): Promise<PipelineFunnelResponse> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<PipelineFunnelResponse>(
        `${this.basePath}/platform/funnel${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getPipelineFunnel failed:', error)
      throw error
    }
  }

  async getUnfilledOrderAging(params?: {
    companyId?: string
    mspAgencyId?: string
  }): Promise<OrderAgingResponse> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<OrderAgingResponse>(
        `${this.basePath}/platform/order-aging${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getUnfilledOrderAging failed:', error)
      throw error
    }
  }

  // ---- Company ----

  async getCompanyOverview(
    companyId: string,
    params?: {startDate?: string; endDate?: string},
  ): Promise<CompanyOverviewResponse> {
    try {
      const query = this.buildQueryString({...params, companyId})
      const response = await this.get<CompanyOverviewResponse>(
        `${this.basePath}/company/overview${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getCompanyOverview failed:', error)
      throw error
    }
  }

  async getCompanyRecentActivity(
    params: CompanyRecentActivityParams,
  ): Promise<PaginatedResponse<RecentActivityItem>> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<PaginatedResponse<RecentActivityItem>>(
        `${this.basePath}/platform/recent-activity${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getCompanyRecentActivity failed:', error)
      throw error
    }
  }

  // ---- MSP ----

  async getMspOverview(params?: MspOverviewParams): Promise<MspOverviewResponse> {
    try {
      const query = this.buildQueryString(params)
      const response = await this.get<MspOverviewResponse>(`${this.basePath}/msp/overview${query}`)
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getMspOverview failed:', error)
      throw error
    }
  }

  /**
   * MSP Branch Comparison (STFFLG-3738): per-branch operational KPIs for the caller's MSP,
   * computed server-side over the selected window (period=week → last 7 days; 12w → trailing
   * 12 weeks) in a single call. `mspAgencyId` is the caller's own MSP agency id.
   */
  async getMspBranchComparison(
    mspAgencyId: string,
    period: MspBranchComparisonPeriod,
    range?: MspBranchComparisonRange,
  ): Promise<MspBranchComparisonResponse> {
    try {
      // `period` is always sent so an older backend keeps working; from/to takes precedence
      // server-side when both are present.
      const query = this.buildQueryString({mspAgencyId, period, ...(range ?? {})})
      const response = await this.get<MspBranchComparisonResponse>(
        `${this.basePath}/msp/branch-comparison${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getMspBranchComparison failed:', error)
      throw error
    }
  }

  // ---- Agency ----

  async getAgencyOverview(
    agencyId: string,
    params?: AgencyOverviewParams,
  ): Promise<AgencyOverviewResponse> {
    try {
      const query = this.buildQueryString({...params, agencyId})
      const response = await this.get<AgencyOverviewResponse>(
        `${this.basePath}/agency/overview${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getAgencyOverview failed:', error)
      throw error
    }
  }

  async getWelcomeCard(agencyId: string): Promise<WelcomeCardResponse> {
    try {
      const query = this.buildQueryString({agencyId})
      const response = await this.get<WelcomeCardResponse>(
        `${this.basePath}/agency/welcome-card${query}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('DashboardApi.getWelcomeCard failed:', error)
      throw error
    }
  }

  // ---- Helpers ----

  private buildQueryString(params?: Record<string, unknown>): string {
    if (!params) return ''
    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value))
      }
    }
    const str = searchParams.toString()
    return str ? `?${str}` : ''
  }
}

export const dashboardApi = new DashboardApiService()
